import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {registerMPElement} from 'common/util/register-element';
import debounce from 'lodash/debounce';
import {Component, ConfigOptions} from 'panel';

import {OmnisearchResult, OmnisearchResultType} from './types';
import {
  getTypeIcon,
  getTypeText,
  getSupportedTypesEnums,
  getTypeTextFromCount,
  trackAndNavigate,
  getTypeTextPlural,
  getFacets,
  saveFacets,
  getTypeKey,
} from './utils';

import template from './index.jade';
import {trackActivation, trackTypeChange, trackResultError, trackFacetFailure, trackSearch} from './tracking';
import {ToggleOption} from 'common/components/toggle/types';

type SelectedToggle = OmnisearchResultType | `all`;

interface OmnisearchState {
  searchText: string;
  isTypesLoading: boolean;
  isResultsLoading: boolean;
  hasSearched: boolean;
  facetedResults: {
    [type: string]: OmnisearchResult[];
  };
  menuItems: any[];
  error: any;
  selectedType: SelectedToggle;
  supportedTypes: string[];
}

class Omnisearch extends Component<OmnisearchState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`, default: false},
      'project-id': {type: `number`},
      'project-name': {type: `string`},
      'workspace-id': {type: `number`},
      'workspace-name': {type: `string`},
    };
  }

  get config(): ConfigOptions<OmnisearchState> {
    return {
      defaultState: {
        isResultsLoading: false,
        isTypesLoading: true,
        hasSearched: false,
        searchText: ``,
        facetedResults: null,
        menuItems: [],
        error: null,
        selectedType: `all`,
        supportedTypes: [],
      },
      template,
      helpers: {
        getItems: () => {
          const {facetedResults, selectedType, isResultsLoading} = this.state;
          const key = getTypeKey(selectedType);
          if (isResultsLoading) {
            return {isLoading: true, items: []};
          }
          if (facetedResults && facetedResults[key]) {
            return {
              items: this.state.menuItems,
            };
          }
          return {items: null};
        },

        getPlaceholderText: () =>
          this.attr(`workspace-id`)
            ? `Search ${this.attr(`workspace-name`)}` // Workspaces have to have a name there is no need to fall back
            : this.attr(`project-name`)
            ? `Search ${this.attr(`project-name`)}`
            : `Search your Mixpanel Project`,

        getResultsItemsEmptyMessage: () =>
          this.state.selectedType === `all`
            ? `We couldn't find any items matching your search`
            : `We couldn't find any ${getTypeTextPlural(this.state.selectedType).toLowerCase()} matching your search`,

        getToggleOptions: (): ToggleOption[] => {
          const {facetedResults, supportedTypes} = this.state;
          const types = getSupportedTypesEnums(supportedTypes);

          return [
            {value: `all`, disabled: false, label: `All`},
            ...types.map((t) => this.toggleOptionFromEnum(facetedResults, t)),
          ];
        },

        getTypesDisplay: () => {
          const {isTypesLoading, supportedTypes} = this.state;
          const types = getSupportedTypesEnums(supportedTypes).map(getTypeTextPlural);

          if (isTypesLoading || types.length === 0) {
            return `Results will come from your Mixpanel.`;
          }
          if (types.length > 2) {
            const last = types.slice(-1).join();
            return `Results will come from your ${types.slice(0, -1).join(`, `)}, and ${last}.`;
          }
          if (types.length === 2) {
            return `Results will come from your ${types.join(` and `)}.`;
          }

          return `Results will come from your ${types[0]}.`;
        },

        handleClose: ({navClosure}) => {
          this.dispatchEvent(new CustomEvent(`close`, {detail: {navClosure}}));
        },

        handleInputInsert: (vnode) => {
          this.searchInput = vnode.elm;
        },

        handleItemClick: ({detail}) => {
          const {selectedType, menuItems} = this.state;
          const {item, modifierKeys} = detail;
          const newTab = item._result.type === OmnisearchResultType.Help || modifierKeys.ctrl || modifierKeys.meta;

          const itemIndex = menuItems.findIndex((i) => i._result.link === item._result.link);
          trackAndNavigate(item._result, itemIndex, {
            openInCurrentWindow: !newTab,
            openInNewWindow: newTab,
            count: menuItems.length,
            selectedType,
          });
          if (!newTab) {
            this.helpers.handleClose({navClosure: true});
          }
        },

        handleResultTypeSelect: ({detail}) => {
          const {facetedResults, selectedType} = this.state;
          if (selectedType === detail.selected) {
            return;
          }
          const menuItems = this.toFilteredMenuItems(detail.selected);

          const newSelectionCount = this.getFacetResultCount(facetedResults, detail.selected);
          const oldSelectionCount = this.getFacetResultCount(facetedResults, this.state.selectedType);

          trackTypeChange(detail.selected, this.state.selectedType, {
            newSelectionCount,
            oldSelectionCount,
          });
          this.update({selectedType: detail.selected, menuItems});

          if (newSelectionCount) {
            this.el.querySelector(`.results`).scrollTop = 0;
          }
        },

        // This event handling is being debounced and due to Shadow DOM, if the value is not
        //  unwrapped by handler in the jade, the target is changed in the setTimeout context
        handleSearchTextChange: debounce((searchText) => {
          if (searchText.length > 1) {
            this.update({searchText, hasSearched: true, facetedResults: null, isResultsLoading: true, error: null});
            this.executeSearch(searchText);
          } else if (searchText.length === 0) {
            this.update({searchText, facetedResults: null, menuItems: []});
          }
        }, 200),
      },
    };
  }

  private searchInput: HTMLInputElement;

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `open` && this.attr(attr)) {
      this.focusInput();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    trackActivation(this.attr(`project-id`));
    this.update({
      supportedTypes: getFacets({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      }),
    });
    this.fetchFacets();
  }

  public async fetchFacets() {
    // normally this is handled by apiClient but we want escape hatches for dev and staging because we don't have
    // data indexed in those environments.
    const tokenOverride = window.localStorage.getItem(`omnisearch_token`);
    const headers = tokenOverride ? {Authorization: `Bearer ${tokenOverride}`} : null;
    try {
      const facets: any[] = await apiClient.get(this.buildFacetTypesUrl(), null, headers ? {headers} : {});
      this.update({
        isTypesLoading: false,
        supportedTypes: saveFacets(
          {
            projectId: this.attr(`project-id`),
            workspaceId: this.attr(`workspace-id`),
          },
          facets.map((r) => r.value),
        ),
      });
    } catch (err) {
      this.update({
        isTypesLoading: false,
      });
      trackFacetFailure(err.message);
    }
  }

  public async executeSearch(searchText: string) {
    let facetedResults = null;
    let error = null;
    try {
      // normally this is handled by apiClient but we want escape hatches for dev and staging because we don't have
      // data indexed in those environments.
      const tokenOverride = window.localStorage.getItem(`omnisearch_token`);
      const headers = tokenOverride ? {Authorization: `Bearer ${tokenOverride}`} : null;

      const {supportedTypes, selectedType} = this.state;
      trackSearch(selectedType, searchText);
      const facetedTypes = [`all`, ...supportedTypes];
      const facetedRequests = await Promise.all(
        facetedTypes.map((type) => apiClient.get(this.buildSearchUrl(type), null, headers ? {headers} : {})),
      );
      facetedResults = facetedTypes.reduce((obj, type, idx) => ({...obj, [type]: facetedRequests[idx]}), {});
    } catch (err) {
      error = err;
      trackResultError(err.message, searchText);
    } finally {
      // ensure that we don't update the results with the wrong values if there were multiple
      //   searches outstanding
      if (this.state.searchText === searchText) {
        this.update({
          facetedResults,
          error,
          isResultsLoading: false,
          menuItems: this.toFilteredMenuItems(this.state.selectedType, facetedResults),
        });
      }
    }
  }

  public toggleOptionFromEnum(
    results: {
      [type: string]: OmnisearchResult[];
    },
    type: OmnisearchResultType,
  ): ToggleOption {
    const key = getTypeKey(type);
    const count = results && results[key] ? results[key].length : 0;
    return {
      value: type,
      disabled: false,
      label: `${getTypeTextFromCount(type, count)} (${count})`,
    };
  }

  private focusInput() {
    setTimeout(() => this.searchInput.focus(), 300);
  }

  private buildSearchUrl(type = `all`) {
    const searchUrl = this.buildUrl();
    const queryParams = {query_string: this.state.searchText, limit: 150};
    const queryString = objToQueryString(type === `all` ? queryParams : {...queryParams, filters: `type:${type}`});
    return `${searchUrl}?${queryString}`;
  }

  private buildFacetTypesUrl() {
    const searchUrl = this.buildUrl();
    const queryString = objToQueryString({attribute: `type`});
    return `${searchUrl}/facets?${queryString}`;
  }

  private buildUrl() {
    // escape hatch
    const urlOverride = window.localStorage.getItem(`omnisearch_url`);
    const projOverride = window.localStorage.getItem(`omnisearch_proj`);
    const workspaceOverride = window.localStorage.getItem(`omnisearch_workspace`);

    if (workspaceOverride || this.attr(`workspace-id`)) {
      return `${urlOverride ? urlOverride : ``}/api/app/workspaces/${workspaceOverride ||
        this.attr(`workspace-id`)}/search`;
    }

    return `${urlOverride ? urlOverride : ``}/api/app/projects/${projOverride || this.attr(`project-id`)}/search`;
  }

  private getFacetResultCount(results, facet) {
    const key = getTypeKey(facet);
    if (results && results[key]) {
      return results[key].length;
    }
    return 0;
  }

  private toFilteredMenuItems(resultType: OmnisearchResultType | `all`, results = this.state.facetedResults) {
    const key = resultType === `all` ? `all` : getTypeKey(resultType);
    if (!results || !results[key]) {
      return [];
    }

    return results[key].map(this.toMenuItem);
  }

  private toMenuItem(item: OmnisearchResult) {
    let subLabel = getTypeText(item.type);
    if (item.type === OmnisearchResultType.Report) {
      if (item.link.includes(`/insights/`)) {
        subLabel = `Insights`;
      } else if (item.link.includes(`/funnels/`)) {
        subLabel = `Funnels`;
      }
    }

    return {
      label: item.name.length > 55 ? `${item.name.substr(0, 50)}...` : item.name,
      subLabel,
      icon: getTypeIcon(item.type),
      _result: item,
    };
  }
}

registerMPElement(`mp-omnisearch`, Omnisearch);
